<template>
  <q-list class="notif-panel">
    <!-- En-tête -->
    <div class="notif-head">
      <q-icon name="notifications" size="22px" class="q-mr-sm" />
      <span class="notif-head__title">Notifications</span>
      <q-badge v-if="appInboxStore.unread" color="red" class="q-ml-sm">{{ appInboxStore.unread }}</q-badge>
      <q-space />
      <q-btn v-if="appInboxStore.hasUnread" flat dense round size="sm" color="white" icon="done_all"
             @click="markAll">
        <q-tooltip>Tout marquer comme lu</q-tooltip>
      </q-btn>
      <q-btn flat dense round size="sm" color="white" icon="close" v-close-popup>
        <q-tooltip>Fermer</q-tooltip>
      </q-btn>
    </div>

    <!-- Notifications système (Web Push) sur cet appareil -->
    <q-item v-if="push.supported && push.permission !== 'denied'">
      <q-item-section avatar>
        <q-icon :name="push.subscribed ? 'notifications_active' : 'notifications_off'"
                :color="push.subscribed ? 'pappus' : 'grey-6'" />
      </q-item-section>
      <q-item-section>
        <q-item-label>{{ push.subscribed ? 'Notifications activées' : 'Activer les notifications' }}</q-item-label>
        <q-item-label v-if="push.subscribed" caption class="text-pappus cursor-pointer" @click.stop="testPush">
          Envoyer un test
        </q-item-label>
        <q-item-label v-else caption>Sur cet appareil, même application fermée</q-item-label>
      </q-item-section>
      <q-item-section side>
        <q-toggle :model-value="push.subscribed" :disable="push.busy" color="pappus"
                  @update:model-value="togglePush" />
      </q-item-section>
    </q-item>
    <q-item v-else-if="push.permission === 'denied'" class="text-grey-7">
      <q-item-section avatar><q-icon name="notifications_off" color="grey-6" /></q-item-section>
      <q-item-section>
        <q-item-label caption>Notifications bloquées par le navigateur (à réautoriser dans ses réglages).</q-item-label>
      </q-item-section>
    </q-item>

    <!-- Son des notifications -->
    <q-item>
      <q-item-section avatar>
        <q-icon :name="soundEnabled ? 'volume_up' : 'volume_off'" :color="soundEnabled ? 'pappus' : 'grey-6'" />
      </q-item-section>
      <q-item-section>
        <q-item-label>Son des notifications</q-item-label>
      </q-item-section>
      <q-item-section side>
        <q-toggle :model-value="soundEnabled" color="pappus" @update:model-value="toggleSound" />
      </q-item-section>
    </q-item>

    <q-separator />

    <!-- Messages reçus -->
    <template v-if="appInboxStore.records.length">
      <q-item v-for="n in appInboxStore.records" :key="n.id"
              clickable v-close-popup @click="open(n)"
              :class="['notif-item', {'notif-item--unread': !n.read}]">
        <q-item-section avatar top>
          <q-icon :name="typeIcon(n)" :color="n.read ? 'grey-6' : 'pappus'" />
        </q-item-section>
        <q-item-section>
          <q-item-label :class="{'text-weight-bold': !n.read}">{{ n.title }}</q-item-label>
          <q-item-label v-if="n.body" caption lines="2" class="notif-item__body">{{ n.body }}</q-item-label>
          <q-item-label caption class="text-grey-6">{{ timeAgo(n.createdAt) }}</q-item-label>
        </q-item-section>
        <q-item-section side>
          <q-btn flat dense round size="sm" icon="close" color="grey-6" @click.stop="remove(n)">
            <q-tooltip>Supprimer</q-tooltip>
          </q-btn>
        </q-item-section>
      </q-item>
    </template>
    <q-item v-else>
      <q-item-section class="text-grey-6 text-center q-py-md">Aucune notification</q-item-section>
    </q-item>
  </q-list>
</template>

<script>
import {mapStores} from "pinia";
import {useAppInboxStore} from "src/stores/appInboxStore";
import AppInboxService from "src/services/appInboxService";
import AppPushService from "src/services/appPushService";
import appNotificationService from "src/services/utils/appNotificationService";

const TYPE_ICONS = {
  task: 'task_alt',
  admin: 'campaign',
  message: 'chat',
  security: 'security',
}

/**
 * Contenu du panneau des notifications (réglages de l'appareil + messages reçus),
 * affiché par appNotificationBell dans un menu (ordinateur) ou une feuille en bas d'écran (mobile)
 */
export default {
  name: 'AppNotificationPanel',

  data() {
    return {
      push: AppPushService.state,
      soundEnabled: appNotificationService.isSoundEnabled(),
    }
  },

  computed: {
    ...mapStores(useAppInboxStore),
  },

  // Monté à chaque ouverture : liste rafraîchie (notifications reçues sans WebSocket actif)
  mounted() {
    AppInboxService.load()
  },

  methods: {
    async togglePush(enabled) {
      try {
        if (enabled) {
          await AppPushService.enable()
          // Confirmation visible : notification de test sur cet appareil
          await AppPushService.sendTest()
        } else {
          await AppPushService.disable()
        }
      } catch (error) {
        appNotificationService.pushError(error)
      }
    },

    async testPush() {
      try {
        const result = await AppPushService.sendTest()
        if (!result.sent) {
          appNotificationService.pushWarning("Aucun appareil abonné : active d'abord les notifications.")
        }
      } catch (error) {
        appNotificationService.pushError(error)
      }
    },

    toggleSound(enabled) {
      this.soundEnabled = enabled
      appNotificationService.setSoundEnabled(enabled)
    },

    // Notification affichée en entier (texte complet, lien éventuel vers la page concernée)
    open(notification) {
      AppInboxService.show(notification)
    },

    markAll() {
      AppInboxService.markAllRead()
    },

    remove(notification) {
      AppInboxService.remove(notification.id)
    },

    typeIcon(notification) {
      return TYPE_ICONS[notification.type] || 'notifications'
    },

    timeAgo(date) {
      if (!date) return ''
      const d = new Date(date)
      const seconds = Math.floor((Date.now() - d.getTime()) / 1000)
      if (seconds < 60) return "à l'instant"
      if (seconds < 3600) return `il y a ${Math.floor(seconds / 60)} min`
      if (seconds < 86400) return `il y a ${Math.floor(seconds / 3600)} h`
      if (seconds < 604800) return `il y a ${Math.floor(seconds / 86400)} j`
      return d.toLocaleDateString()
    },
  },
}
</script>

<style scoped lang="scss">
.notif-panel {
  padding: 0;
}
.notif-head {
  display: flex;
  align-items: center;
  padding: 14px 12px 12px 16px;
  color: white;
  background: linear-gradient(135deg, #6b9a3c 0%, #3f5f22 100%);

  &__title {
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.3px;
  }
}
.notif-item--unread {
  background: rgba(107, 154, 60, 0.08);
}
.notif-item__body {
  white-space: pre-line;
}
.notif-item .q-btn {
  opacity: 0.6;
  transition: opacity 0.2s ease;
}
.notif-item:hover .q-btn {
  opacity: 1;
}
</style>
